import { useCbhFlag } from "@src/appV2/FeatureFlags";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useMemo } from "react";

interface UseContractorAgreementReSigningParams {
  worker: Pick<Worker, "signedContractorAgreements"> | undefined;
}

/**
 * Note that this feature flag is not meant to be targetted to all workers,
 * but rather a subset of workers. It was originally used to mitigate a situation where there
 * was an issue with the contract affecting a small subset of workers and we needed them to re-sign the agreement.
 * For cases where you want all workers to re-sign the agreement, you can simply add a new version of the contract agreement.
 */
export function useContractorAgreementReSigning({ worker }: UseContractorAgreementReSigningParams) {
  const { enabled: isReSigningEnabled, cutoffTimestamp } = useCbhFlag(
    CbhFeatureFlag.CONTRACTOR_AGREEMENT_RE_SIGNING,
    {
      defaultValue: {
        enabled: false,
        cutoffTimestamp: "2100-01-01T00:00:00.000Z",
      },
    }
  );

  const needsReSigning = useMemo(() => {
    if (!isReSigningEnabled) {
      return false;
    }

    if (!worker?.signedContractorAgreements?.length) {
      return false;
    }

    const latestAgreement = worker.signedContractorAgreements.sort(
      (a, b) => new Date(b.signedDate).getTime() - new Date(a.signedDate).getTime()
    )[0];

    const signedAt = new Date(latestAgreement.signedDate);
    const cutoffDate = new Date(cutoffTimestamp);

    // if they signed before the cutoff date, it means they signed before the feature flag was rolled out
    // to the current cohort of workers, so they need to re-sign after which this expression will be false
    return signedAt < cutoffDate;
  }, [cutoffTimestamp, isReSigningEnabled, worker?.signedContractorAgreements]);

  return {
    needsReSigning,
  };
}
